import { apiRequest } from "./client";

export interface CreateCheckoutSessionRequest {
  products: Array<{
    sallaProductId: string;
    quantity?: number;
    identifierType?: "id" | "variant_id" | "sku";
    options?: Array<{
      id: string | number;
      value: Array<string | number>;
    }>;
  }>;
  reportId?: string;
  shippingAddressId?: string;
  shippingAddress?: CheckoutAddressPayload;
  shippingRateId?: string;
  shippingCompanyId?: string;
}

export interface CheckoutAddressPayload {
  label?: string;
  recipientName?: string;
  phoneNumber?: string;
  countryId: string;
  cityId: string;
  districtId?: string;
  districtName?: string;
  address: string;
  addressLine?: string;
  streetNumber?: string;
  geoCoordinates?: string;
  postalCode?: string;
  isDefault?: boolean;
}

export interface CheckoutAddress extends CheckoutAddressPayload {
  id: string;
  label: string;
  recipientName: string;
  phoneNumber: string;
  districtId: string;
  districtName: string;
  addressLine: string;
  streetNumber: string;
  geoCoordinates: string;
  postalCode: string;
  isDefault: boolean;
}

export interface CheckoutShippingRate {
  id: string;
  companyId: string;
  title: string;
  amount: number;
  currency: string;
  logo?: string | null;
  deliveryTime?: string | null;
}

export interface CheckoutCountry {
  id: string;
  name: string;
  nameEn: string;
  code: string;
  mobileCode: string;
}

export interface CheckoutCity {
  id: string;
  name: string;
  nameEn: string;
  countryId: string;
}

export interface CheckoutDistrict {
  id: string;
  name: string;
  nameEn: string;
  cityId: string;
}

export interface CheckoutProductOption {
  id: string;
  name: string;
  required: boolean;
  values: Array<{
    id: string;
    name: string;
    isDefault: boolean;
    isOutOfStock: boolean;
    stockQuantity: number | null;
  }>;
}

export interface CreateCheckoutSessionResponse {
  success: boolean;
  data: {
    requestId?: string;
    sallaOrderId?: string | null;
    sallaOrderReferenceId?: string | null;
    cartId: string | null;
    checkoutUrl: string | null;
    cartUrl?: string | null;
    redirectUrl: string | null;
    redirectMode: "store_cart" | "direct_checkout" | "manual_payment_request" | "salla_order";
    itemsCount: number;
    storeName?: string | null;
  };
  message: string;
}

export interface CheckoutStoreResponse {
  connected: boolean;
  storeId: string;
  storeName?: string | null;
  storeDomain?: string | null;
  displayName: string;
  redirectMode: "store_cart" | "direct_checkout" | "manual_payment_request" | "salla_order";
}

/**
 * Create a checkout session with multiple products
 * This function will:
 * 1. Send products to backend
 * 2. Backend creates a cart in Salla
 * 3. Backend adds all products to the cart
 * 4. Returns checkout URL to redirect user
 */
export const createCheckoutSession = async (
  products: CreateCheckoutSessionRequest["products"],
  options: Omit<CreateCheckoutSessionRequest, "products"> = {}
): Promise<CreateCheckoutSessionResponse> => {
  const data = await apiRequest<CreateCheckoutSessionResponse["data"]>("/checkout/create-session", {
    method: "POST",
    body: { products, ...options },
  });

  return {
    success: true,
    data,
    message: "Checkout session created successfully",
  };
};

export const fetchCheckoutStore = async (): Promise<CheckoutStoreResponse> => {
  return apiRequest<CheckoutStoreResponse>("/checkout/store");
};

export const fetchCheckoutProductOptions = async (productId: string): Promise<CheckoutProductOption[]> => {
  return apiRequest<CheckoutProductOption[]>(`/checkout/products/${encodeURIComponent(productId)}/options`);
};

export const fetchCheckoutAddresses = async (reportId?: string): Promise<CheckoutAddress[]> => {
  return apiRequest<CheckoutAddress[]>("/checkout/addresses", {
    params: { reportId },
  });
};

export const saveCheckoutAddress = async (
  address: CheckoutAddressPayload,
  reportId?: string
): Promise<CheckoutAddress> => {
  return apiRequest<CheckoutAddress>("/checkout/addresses", {
    method: "POST",
    body: { address, reportId },
  });
};

export const fetchCheckoutShippingRates = async (
  address: Pick<CheckoutAddressPayload, "countryId" | "cityId" | "postalCode">
): Promise<CheckoutShippingRate[]> => {
  return apiRequest<CheckoutShippingRate[]>("/checkout/shipping-rates", {
    method: "POST",
    body: { address },
  });
};

export const fetchCheckoutCountries = async (): Promise<CheckoutCountry[]> => {
  return apiRequest<CheckoutCountry[]>("/checkout/countries");
};

export const fetchCheckoutCities = async (countryId: string): Promise<CheckoutCity[]> => {
  return apiRequest<CheckoutCity[]>(`/checkout/countries/${encodeURIComponent(countryId)}/cities`);
};

export const fetchCheckoutDistricts = async (cityId: string): Promise<CheckoutDistrict[]> => {
  return apiRequest<CheckoutDistrict[]>(`/checkout/cities/${encodeURIComponent(cityId)}/districts`);
};

/**
 * Redirect user to Salla checkout
 * This is called after products are added to cart
 */
export const redirectToCheckout = (redirectUrl: string) => {
  window.open(redirectUrl, "_blank", "noopener,noreferrer");
};

/**
 * Helper function to prepare products for checkout
 * Filters products with stock > 0 and formats for API
 */
export const prepareProductsForCheckout = (
  products: Array<{
    id: string;
    stockQuantity: number;
    [key: string]: any;
  }>
) => {
  return products
    .filter((product) => product.stockQuantity > 0)
    .map((product) => ({
      sallaProductId: product.id,
      quantity: 1,
      identifierType: "id" as const,
    }));
};
